'use client';

/**
 * Shared "PRICE USD  DATE" hover pill used by PopupChart and MiniChart.
 *
 * Positioned at the SVG's actual pixel coordinates rather than viewBox
 * coordinates, so it stays correctly anchored to the cursor regardless
 * of how the SVG was stretched. Clamps the pill's center so it stays
 * inside the chart bounds — near the leftmost / rightmost bars the
 * centered transform alone would push half the label outside the chart,
 * so we measure the actual rendered pill width via useLayoutEffect and
 * pin the center to `[halfW + inset, rect.width − halfW − inset]`.
 * Hidden on the very first paint (width=0) so the unclamped position
 * never flashes.
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { formatCurrency } from '@/lib/utils';

export interface ChartHoverTooltipProps {
  /** The SVG element being hovered — used for getBoundingClientRect()
   *  to translate viewBox coordinates into actual screen pixels. */
  svg: SVGSVGElement;
  /** Cursor x in viewBox units (not screen pixels). */
  xPx: number;
  /** ViewBox width of the SVG. The ratio `screenWidth / viewW` lets us
   *  map xPx into screen-pixel space. */
  viewW: number;
  /** Hovered bar's close price. */
  value: number;
  /** Hovered bar's date string (intraday `YYYY-MM-DD HH:MM[:SS]` or
   *  daily `YYYY-MM-DD`). */
  date: string;
  /** Pixel inset on either side. Defaults to 4. */
  inset?: number;
  /** Top offset in pixels. Defaults to 4. */
  top?: number;
}

/** Format "Oct 21, 2025" for daily bars or "Oct 21 09:30" for intraday. */
function formatHoverDate(date: string): string {
  if (date.includes(' ')) {
    const d = new Date(date.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return date;
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  }
  // Daily date is calendar-only — anchor + render in UTC so the hover pill
  // shows the bar's own date, not the previous day for US viewers.
  const d = new Date(date + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function ChartHoverTooltip({
  svg, xPx, viewW, value, date, inset = 4, top = 4,
}: ChartHoverTooltipProps) {
  const pillRef = useRef<HTMLSpanElement>(null);
  const [pillW, setPillW] = useState(0);

  useLayoutEffect(() => {
    if (!pillRef.current) return;
    const w = pillRef.current.offsetWidth;
    if (w && w !== pillW) setPillW(w);
  });

  const rect = svg.getBoundingClientRect();
  const ratio = rect.width / viewW;
  const desiredCenter = xPx * ratio;

  const halfW = pillW / 2;
  const minCenter = halfW + inset;
  const maxCenter = rect.width - halfW - inset;
  const clampedCenter = pillW > 0 && maxCenter > minCenter
    ? Math.max(minCenter, Math.min(maxCenter, desiredCenter))
    : desiredCenter;

  return (
    <div
      className="absolute pointer-events-none text-xs font-mono whitespace-nowrap"
      style={{
        left: `${clampedCenter}px`,
        top: `${top}px`,
        transform: 'translateX(-50%)',
        visibility: pillW > 0 ? 'visible' : 'hidden',
      }}
    >
      <span ref={pillRef} className="bg-card-bg/95 border border-default text-fg rounded px-1.5 py-0.5">
        <span className="font-semibold text-white">{formatCurrency(value)} </span>
        <span className="text-fg-subtle ml-1">{formatHoverDate(date)}</span>
      </span>
    </div>
  );
}
